/**
 * The dock's own stopwatch: where a spoken turn's time actually went.
 *
 * One turn passes through a dozen hands — the device, the relay, the speech
 * vendor, the parser, the preview endpoint, the read-back, the apply — and
 * until now the only numbers anybody had were the server's. The server can see
 * its own arms and nothing else: it cannot see how long the microphone took to
 * open, how long the browser sat on a final transcript before the card drew, or
 * how long the person listened to a sentence being read back. Those are the
 * parts the user experiences as "voice is slow", and they are only observable
 * here.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE. Every number that leaves here is
 * the difference between two readings of `performance.now()` taken in THIS
 * page. Never a wall-clock difference, and never a client stamp minus a server
 * stamp. The two clocks are not the same clock: they drift, they are set by
 * different daemons, and a user whose laptop is 40 s fast would post a 40 s
 * "parse" that is really a clock offset — which is worse than no measurement,
 * because it looks like a measurement. `Date.now()` is therefore not used in
 * this file at all, and on a platform with no `performance.now()` the ledger
 * measures NOTHING and says so by omission rather than substituting a clock of
 * a different kind wearing the same name.
 *
 * THE SPANS ARE A CLOSED LIST. `SPANS` below is the contract with the backend
 * (FIX-B9 item 3): the server drops any key it does not know and clamps every
 * value into `0..MAX_SPAN_MS`, so the ledger clamps to exactly the same bounds.
 * A client that sent a number the server then changed would leave the footer on
 * screen and the row in the log disagreeing about the same turn, which is the
 * one failure a timing feature cannot survive.
 *
 * ABSENT IS NOT ZERO, the house rule, applies here with teeth. A span whose two
 * marks were not both taken is OMITTED from the snapshot — it is not sent as 0.
 * "The read-back never played" and "the read-back played instantly" are
 * different facts about a turn, and the percentile card on the admin page
 * divides by `n`: one invented zero per quiet turn would drag every p50 towards
 * nothing and hide the slow stage the card exists to find.
 *
 * FIRST WRITE WINS, and that is a load-bearing decision rather than a
 * defensive one. Several of these events happen more than once inside one turn:
 * a proposal is read back and then a receipt is read back, both through the
 * same `speak` path, so `ttsFirstAudible` is offered twice. The span
 * `previewVisible→ttsFirstAudible` means the proposal's read-back — the first
 * one after the card drew — and last-write-wins would silently redefine it as
 * the receipt's. The single exception is `speechEnd`, which is an ESTIMATE
 * re-taken on every partial (the relay never reports when the speaker stopped,
 * so the closest observable is the last partial before the final that
 * supersedes it); callers ask for that explicitly with `{ replace: true }`.
 */

/**
 * The arrow is the contract's own separator (FIX-B9 item 3), so it is written
 * once here and the key strings are derived rather than typed out twice. A key
 * spelled differently in two files is a span that silently never aggregates.
 */
export const SPAN_SEPARATOR = "→"; // →

/**
 * The closed span list, in the order a turn walks through it.
 *
 * Each entry is `[from, to]`, and the wire key is `from→to`. The order is the
 * order of the turn rather than of interest, because that is the order the
 * admin card's reader reconstructs the journey in when the numbers look wrong.
 */
export const SPANS = Object.freeze([
  ["captureRequested", "captureLive"],
  ["speechEnd", "sttFinal"],
  ["sttFinal", "proposal"],
  ["proposal", "previewVisible"],
  ["previewVisible", "ttsFirstAudible"],
  ["ttsFirstAudible", "ttsEnded"],
  ["confirmSpeechStart", "confirmFinal"],
  ["confirm", "applied"],
  ["applied", "receiptVisible"],
].map((pair) => Object.freeze(pair)));

/** `captureRequested→captureLive`, … — the wire keys, in turn order. */
export const TIMING_KEYS = Object.freeze(
  SPANS.map(([from, to]) => `${from}${SPAN_SEPARATOR}${to}`)
);

/** Every mark a span needs, de-duplicated. Anything else is not markable. */
export const TIMING_MARKS = Object.freeze([
  ...new Set(SPANS.reduce((acc, pair) => acc.concat(pair), [])),
]);

/**
 * The server's own ceiling (FIX-B9 item 3), repeated here on purpose.
 *
 * Two minutes is longer than any stage of a voice turn can legitimately be: a
 * span past it is a page that was backgrounded, a laptop that slept, or a mark
 * left over from a turn that never finished. Clamping rather than dropping
 * keeps the turn countable — the stage really did take "at least this long" —
 * and clamping to the SERVER's number is what stops the screen and the log
 * disagreeing.
 */
export const MAX_SPAN_MS = 120000;

/** Sub-millisecond resolution kept, and no further: `performance.now()` is
 *  itself coarsened to about a tenth of a millisecond by every browser that
 *  takes Spectre seriously, so more decimals would be digits we did not measure. */
const DECIMALS = 1;

const MARK_SET = new Set(TIMING_MARKS);

function round(n) {
  const factor = 10 ** DECIMALS;
  return Math.round(n * factor) / factor;
}

/**
 * A monotonic reading, or null where there is no monotonic clock.
 *
 * Null rather than `Date.now()`: see the header. Every browser this dock runs
 * in has `performance.now()`, and node has it too, so the null branch is for a
 * hardened environment that has removed it — where the honest report is no
 * measurement at all.
 */
export function monotonicNow() {
  if (typeof performance === "undefined" || typeof performance.now !== "function")
    return null;
  const value = performance.now();
  return Number.isFinite(value) ? value : null;
}

/**
 * One turn's marks and the spans between them.
 *
 * Deliberately not reactive and deliberately not a store: the dock owns one of
 * these, projects the three numbers its footer shows out of it, and sends the
 * snapshot. Keeping it a plain object is what lets `scripts/test-voice-timing.js`
 * drive every case with an injected clock, which is the only way to test a
 * stopwatch without waiting for real time to pass.
 */
export class TimingLedger {
  /**
   * @param {() => (number|null)} [clock] reads the monotonic clock. Injected by
   *   the test; production never passes one.
   */
  constructor(clock) {
    this.clock = typeof clock === "function" ? clock : monotonicNow;
    this.marks = Object.create(null);
  }

  /**
   * Stamp a mark with the monotonic clock.
   *
   * Unknown keys are IGNORED rather than stored. The span list is closed and
   * the server drops what it does not know, so a mark that no span reads would
   * be a stamp nobody can ever see — better to make the typo inert here than to
   * let it look like it worked.
   *
   * @param {string} key one of `TIMING_MARKS`
   * @param {{replace?: boolean}} [options] `replace` re-stamps a mark already
   *   taken. Only `speechEnd` uses it; see the header.
   * @returns {number|null} the stamp now held for that mark, or null.
   */
  mark(key, { replace = false } = {}) {
    if (!MARK_SET.has(key)) return null;
    if (!replace && this.marks[key] !== undefined) return this.marks[key];
    const at = this.clock();
    if (!Number.isFinite(at)) return this.marks[key] === undefined ? null : this.marks[key];
    this.marks[key] = at;
    return at;
  }

  /** Whether a mark has been taken this turn. */
  has(key) {
    return this.marks[key] !== undefined;
  }

  /** The stamp held for a mark, or null. Exposed for the tests and for guards. */
  at(key) {
    return this.marks[key] === undefined ? null : this.marks[key];
  }

  /**
   * The span between two marks, in ms, or null when either was not taken.
   *
   * Clamped into `0..MAX_SPAN_MS`. The lower bound is reachable only when the
   * two marks were taken out of order — `performance.now()` never goes
   * backwards — which means the ledger was driven wrongly rather than that
   * anything happened in negative time; a negative duration is not a quantity
   * the log or the screen has any way to render, and the server would clamp it
   * to the same 0 regardless.
   *
   * @returns {number|null}
   */
  span(from, to) {
    const start = this.marks[from];
    const end = this.marks[to];
    if (start === undefined || end === undefined) return null;
    return round(Math.min(MAX_SPAN_MS, Math.max(0, end - start)));
  }

  /**
   * The closed-key object to send: only spans whose BOTH marks exist.
   *
   * A turn that was cancelled before the card drew sends the three spans it
   * really has, not nine keys of which six are lies. The server merges the
   * snapshots of one turn across the preview body, the apply body and the
   * `voice:timing` event, so a key that is absent now and present later is the
   * normal case rather than a gap.
   */
  snapshot() {
    const out = {};
    for (const [from, to] of SPANS) {
      const value = this.span(from, to);
      if (value !== null) out[`${from}${SPAN_SEPARATOR}${to}`] = value;
    }
    return out;
  }

  /** Every mark dropped. Called at the start of each turn, never mid-turn. */
  reset() {
    this.marks = Object.create(null);
  }
}

/** The ledger the dock holds. A function so the dock reads as a constructor call. */
export function createTimingLedger(clock) {
  return new TimingLedger(clock);
}

/** True when a snapshot has at least one measured span. */
export function hasTiming(timing) {
  return !!timing && typeof timing === "object" && Object.keys(timing).length > 0;
}

/**
 * A request body with `timing` added, and only when there is something to add.
 *
 * The same shape of decision `withTurnLink` makes about turn ids, for the same
 * reason: an EMPTY `timing: {}` on every parse body is a key the server has to
 * open, merge and store on turns that measured nothing. Absent says "this turn
 * has no spans yet"; empty says "this turn has no spans", and only one of those
 * is true of a preview sent before the card has drawn.
 *
 * The body is copied, never mutated — a retry re-sends the caller's object.
 *
 * @param {object} body the request body so far
 * @param {object|null} [timing] a `TimingLedger.snapshot()`
 * @returns {object} a new body
 */
export function withTiming(body, timing) {
  const base = body && typeof body === "object" ? body : {};
  return hasTiming(timing) ? { ...base, timing: { ...timing } } : { ...base };
}
